/* eslint-disable arrow-body-style */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useHistory } from 'react-router';
import { FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';
import { CircularProgress, Container, Grid, InputAdornment, TextField, Theme, useTheme } from '@material-ui/core';
import { useAlert, useUser } from '../../../contexts';
import api, { Referral } from '../../../api/api';
import { StyledText } from '../../../components/StyledText';
import StatusChip from '../../../components/StatusChip';
import Button from '../../../components/Button';
import ExportToCsvButton from '../../../components/ExportToCsvButton';


const useStyles = makeStyles<Theme>((theme: Theme) => ({
  root: {
    paddingLeft: '0px',
    paddingRight: '0px'
  },
  titleWrapper: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: '20px'
  },
  title: {
  },
  helperText: {
    margin: '20px 0px'
  },
  topWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexDirection: 'row',
    width: 'auto',
    flexWrap: 'wrap'
  },
  searchWrapper: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    flexDirection: 'row',
    width: 'auto',
    flexWrap: 'wrap'
  },
  searchFromField: {
    display: 'flex',
    padding: '10px 0px',
    width: 'auto',
    maxWidth: '250px',
    marginRight: '20px',
    '& .MuiInputBase-input': {
      textAlign: 'center'
    }
  },
  table: {
    flexDirection: 'column'
  },
  tableRowWrapper: {
    backgroundColor: 'white',
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    height: 'auto',
    minHeight: '50px',
    padding: '5px 20px',
    margin: '5px 0px'
  },
  tableHeaderWrapper: {
    margin: '15px 0 0 0',
    backgroundColor: `${theme.palette.primary.light}`,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    minHeight: '50px',
    padding: '5px 20px'
  },
  tableFooterWrapper: {
    backgroundColor: `${theme.palette.primary.light}`,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '50px',
    padding: '5px 10px'
  },
  tableRowContent: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    flexGrow: 1,
    width: '100%',
    padding: theme.spacing(0, 2)
  },
  tableContentWraper: {
    alignItems: 'center',
    justifyContent: 'center'
  },
  contactName: {
    display: 'inline-flex',
    justifyContent: 'flex-start',
    cursor: 'pointer',
    wordBreak: 'break-all',
    alignItems: 'center',
    '&:hover': {
      color: theme.palette.secondary.main
    }
  },
  exportButton: {
    width: 'auto',
    padding: 0,
    justifyContent: 'flex-start',
    backgroundColor: 'rgba(1,1,1,0)',
    '&:hover': {
      backgroundColor: 'rgba(1,1,1,0)'
    }
  },
  loadingWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '20px 0px'
  }
}));

interface ReferralRowProps {
  referral: Referral
}


const ReferralRow: React.FC<ReferralRowProps> = ({ referral }) => {
  const { investorCompany, investorName, status, entCompany, lastUpdated } = referral;
  const theme = useTheme();
  const styles = useStyles(theme);
  const history = useHistory();

  const goToEntProfile = () => {
    const { entProfileId } = referral;
    history.push(`/entrepreneur/${entProfileId}`);
  };

  const goToInvestorProfile = () => {
    const { investorProfileId } = referral;
    history.push(`/investor/${investorProfileId}`);
  };

  const lastUpdateText = useMemo(() => {
    const asDate = new Date(lastUpdated);
    return asDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  }, [lastUpdated]);


  return (
    <div className={styles.tableRowWrapper}>
      <Grid container item spacing={1} xs='auto' alignContent='center' alignItems='center' className={styles.tableRowContent} wrap='nowrap'>
        <Grid item xs={2}>
          <StyledText>{lastUpdateText}</StyledText>
        </Grid>
        <Grid item xs={4}>
          <StyledText className={styles.contactName} onClick={goToInvestorProfile}>{`${investorName} - ${investorCompany}`}</StyledText>
        </Grid>
        <Grid item xs={4}>
          <StyledText className={styles.contactName} align='left' onClick={goToEntProfile}>{`${entCompany}`}</StyledText>
        </Grid>
        <Grid item xs={1}>
          <StatusChip status={status} />
        </Grid>
      </Grid>
    </div>

  );
};

const ReferralHeader = () => {
  const theme = useTheme();
  const styles = useStyles(theme);
  return (
    <div className={styles.tableHeaderWrapper}>
      <Grid container spacing={1} alignContent='center' alignItems='center' className={styles.tableRowContent} wrap='nowrap'>
        <Grid item xs={2}>
          <StyledText>Last updated</StyledText>
        </Grid>
        <Grid item xs={4}>
          <StyledText>Investor</StyledText>
        </Grid>
        <Grid item xs={4}>
          <StyledText align='left'>Deal company</StyledText>
        </Grid>
        <Grid item xs={1}>
          <StyledText align='left'>Status</StyledText>
        </Grid>
      </Grid>
    </div>
  );
};

interface TableContentProps {
  referrals: Referral[]
}

const ReferralTableContent:React.FC<TableContentProps> = ({ referrals }) => {
  const theme = useTheme();
  const styles = useStyles(theme);

  return (
    <Grid container item xs={12} className={styles.tableContentWraper}>
      {
        referrals.length > 0
          ? referrals.map((referral, idx) => {
            return (
              <Grid item xs={12} key={idx}>
                <ReferralRow referral={referral} />
              </Grid>
            );
          })
          : <StyledText style={{ marginTop: '20px' }}>No results found.</StyledText>
      }
    </Grid>
  );
};

interface ReferralFooterProps {
  handleClick: () => void
}

const ReferralFooter: React.FC<ReferralFooterProps> = ({ handleClick }) => {
  const theme = useTheme();
  const styles = useStyles(theme);
  return (
    <div className={styles.tableFooterWrapper}>
      <StyledText>More results are available. You can export to CSV format to review them all. </StyledText>
      <ExportToCsvButton onClick={handleClick} />
    </div>
  );
};

const ConnectorActivityConsole = () => {
  const theme = useTheme();
  const styles = useStyles(theme);
  const { getAccessToken } = useUser();
  const { postNewAlert } = useAlert();

  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [csvForDownlod, setCsvForDownload] = useState<string|null>(null);
  const [searchFromDays, setSearchFromDays] = useState<number|null>(null);
  const [moreResultsAvailable, setMoreResultsAvailable] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchReferrals = useCallback(async (days: number | null) => {
    setLoading(true);
    setMoreResultsAvailable(false);
    try {
      const token = await getAccessToken();
      const { referrals: fetchedReferrals, csv, moreResults } = await api.getReferralsFromPastDays(token, days);
      setReferrals(fetchedReferrals);
      setCsvForDownload(csv);
      setMoreResultsAvailable(moreResults);
    } catch (e:unknown) {
      postNewAlert('Something went wrong. Referral activity could not be fetched.');
    } finally {
      setLoading(false);
    }
  }, [getAccessToken, postNewAlert]);

  useEffect(() => {
    fetchReferrals(searchFromDays);
  }, [fetchReferrals, searchFromDays]);

  const handleFormSubmit = (e: any) => {
    setSearchFromDays(formik.values.searchFrom);
  };

  const handleExportClick = useCallback(() => {
    if (csvForDownlod === null) {
      return;
    }
    const elId = 'dl-Link';
    const blob = new Blob([csvForDownlod as any], { type: 'text/csv;charset=utf-8' });
    let fakeLink = document.getElementById(elId);
    if (fakeLink == null) {
      fakeLink = document.createElement('a');
      fakeLink.id = elId;
      fakeLink.style.display = 'none';
      document.body.appendChild(fakeLink);
    }
    fakeLink.setAttribute('href', URL.createObjectURL(blob));
    fakeLink.setAttribute('download', 'referrals.csv');
    fakeLink.click();
  }, [csvForDownlod]);

  const formik = useFormik({
    initialValues: { searchFrom: null },
    validationSchema: Yup.object().shape({
      searchFrom: Yup.number().nullable().default(null)
    }),
    onSubmit: handleFormSubmit
  });

  return (
    <Container maxWidth={false} className={styles.root}>
      <StyledText className={styles.title} variant='h1'>Your referral activity</StyledText>
      <Grid container className={styles.table}>
        <Grid item xs={12}>
          <StyledText className={styles.helperText}>
            The table shows a limited preview and summary of your referral history. You can download a full, detailed history as a CSV file by clicking the button below.
          </StyledText>
          <FormikProvider value={formik}>
            <div className={styles.topWrapper}>
              <ExportToCsvButton onClick={handleExportClick} />
              <div className={styles.searchWrapper}>
                <TextField
                  id='searchFrom'
                  className={styles.searchFromField}
                  label=''
                  value={formik.values.searchFrom}
                  InputProps={{
                    type: 'tel',
                    value: formik.values.searchFrom || '',
                    onChange: formik.handleChange,
                    startAdornment: <InputAdornment position='start'>Search from</InputAdornment>,
                    endAdornment: <InputAdornment position='end'>days ago</InputAdornment>
                  }}
                />
                <Button type='submit' disabled={!formik.isValid || !formik.dirty} onClick={handleFormSubmit}>Filter</Button>
              </div>
            </div>
          </FormikProvider>
        </Grid>
        <Grid item xs={12}>
          <ReferralHeader />
        </Grid>
        {
          loading
            ? (
              <Grid item xs={12} className={styles.loadingWrapper}>
                <CircularProgress />
              </Grid>
            )
          // eslint-disable-next-line arrow-body-style
            : <ReferralTableContent referrals={referrals} />
        }
        {
          moreResultsAvailable
            ? (
              <Grid item xs={12}>
                <ReferralFooter handleClick={handleExportClick} />
              </Grid>
            )
            : null
        }
      </Grid>
    </Container>
  );
};

export default ConnectorActivityConsole;
